import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ALL_DAYS,
  CONFIG_LIMITS,
  DAY_LABELS,
  type DeviceConfig,
  type DeviceDetail,
  type Schedule,
  type ScheduleInput,
} from '@irrigazione/shared';
import { api } from '../api/client';
import { Field, Notice } from './ui';
import { formatDays, formatTime } from '../format';

export function ModeTab({ device }: { device: DeviceDetail }) {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<DeviceConfig>(device.config);
  const [dirty, setDirty] = useState(false);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['device', device.id] });
    queryClient.invalidateQueries({ queryKey: ['devices'] });
  };

  const save = useMutation({
    mutationFn: () => api.saveConfig(device.id, config),
    onSuccess: () => {
      setDirty(false);
      refresh();
    },
  });

  const patch = (values: Partial<DeviceConfig>) => {
    setConfig((current) => ({ ...current, ...values }));
    setDirty(true);
  };

  return (
    <div className="stack">
      <section className="card stack">
        <h3 style={{ margin: 0, fontSize: 15 }}>Modalità di lavoro</h3>

        <div className="row row-wrap" role="radiogroup" aria-label="Modalità">
          {(
            [
              ['off', 'Disattivata', 'Il controller riporta soltanto lo stato'],
              ['interval', 'A intervalli', 'Risveglio ogni X minuti, irrigazione di Y minuti'],
              ['schedule', 'Programmata', 'Una o più fasce settimanali'],
            ] as const
          ).map(([value, label, hint]) => (
            <button
              key={value}
              role="radio"
              aria-checked={config.mode === value}
              className={`btn${config.mode === value ? ' btn--primary' : ''}`}
              onClick={() => patch({ mode: value })}
              title={hint}
            >
              {label}
            </button>
          ))}
        </div>

        {config.mode === 'interval' && (
          <div className="fields">
            <Field label="Risveglio ogni (minuti)">
              <input
                type="number"
                min={CONFIG_LIMITS.intervalMinutes.min}
                max={CONFIG_LIMITS.intervalMinutes.max}
                value={config.intervalMinutes}
                onChange={(e) => patch({ intervalMinutes: Number(e.target.value) })}
              />
            </Field>
            <Field label="Irrigazione per (minuti)">
              <input
                type="number"
                min={CONFIG_LIMITS.intervalDurationMinutes.min}
                max={CONFIG_LIMITS.intervalDurationMinutes.max}
                value={config.intervalDurationMinutes}
                onChange={(e) => patch({ intervalDurationMinutes: Number(e.target.value) })}
              />
            </Field>
          </div>
        )}

        {config.mode !== 'interval' && (
          <div className="fields">
            <Field label="Invio stato ogni (minuti)">
              <input
                type="number"
                min={CONFIG_LIMITS.reportIntervalMinutes.min}
                max={CONFIG_LIMITS.reportIntervalMinutes.max}
                value={config.reportIntervalMinutes}
                onChange={(e) => patch({ reportIntervalMinutes: Number(e.target.value) })}
              />
            </Field>
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' }} />

        <div className="stack" style={{ gap: 8 }}>
          <div className="row row-wrap" style={{ justifyContent: 'space-between' }}>
            <label htmlFor="threshold">Soglia di umidità</label>
            <label className="row small" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={config.moistureThresholdPct == null}
                onChange={(e) => patch({ moistureThresholdPct: e.target.checked ? null : 40 })}
              />
              Irriga sempre, ignora l&apos;umidità
            </label>
          </div>
          {config.moistureThresholdPct != null && (
            <>
              <input
                id="threshold"
                type="range"
                min={0}
                max={100}
                value={config.moistureThresholdPct}
                onChange={(e) => patch({ moistureThresholdPct: Number(e.target.value) })}
              />
              <p className="tiny secondary" style={{ margin: 0 }}>
                L&apos;irrigazione parte solo se l&apos;umidità misurata è sotto il{' '}
                <strong>{config.moistureThresholdPct} %</strong>. Se il sensore non è leggibile
                l&apos;irrigazione viene saltata e annotata nello storico.
              </p>
            </>
          )}
        </div>

        {device.type === 'standalone' && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="row row-wrap" style={{ justifyContent: 'space-between' }}>
              <label htmlFor="water-min">Livello minimo serbatoio</label>
              <label className="row small" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={config.waterLevelMinPct == null}
                  onChange={(e) => patch({ waterLevelMinPct: e.target.checked ? null : 10 })}
                />
                Nessuna protezione
              </label>
            </div>
            {config.waterLevelMinPct != null && (
              <>
                <input
                  id="water-min"
                  type="range"
                  min={0}
                  max={100}
                  value={config.waterLevelMinPct}
                  onChange={(e) => patch({ waterLevelMinPct: Number(e.target.value) })}
                />
                <p className="tiny secondary" style={{ margin: 0 }}>
                  Sotto il <strong>{config.waterLevelMinPct} %</strong> la pompa non parte. Il
                  controller ricontrolla anche durante l&apos;irrigazione e si ferma se il serbatoio
                  si svuota.
                </p>
              </>
            )}
          </div>
        )}

        <Field label="Durata massima di una singola irrigazione (minuti)">
          <input
            type="number"
            min={CONFIG_LIMITS.maxDurationMinutes.min}
            max={CONFIG_LIMITS.maxDurationMinutes.max}
            value={config.maxDurationMinutes}
            onChange={(e) => patch({ maxDurationMinutes: Number(e.target.value) })}
            style={{ maxWidth: 160 }}
          />
        </Field>

        <div className="row">
          <button
            className="btn btn--primary"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            Salva configurazione
          </button>
          {dirty && <span className="tiny muted">Modifiche non salvate</span>}
          {save.isSuccess && !dirty && <span className="tiny muted">Salvato</span>}
        </div>
        {save.error && <Notice variant="error">{(save.error as Error).message}</Notice>}
      </section>

      {device.type === 'lora' && (
        <ValveDirectionCard
          invert={config.valveInvert}
          onChange={(valveInvert) => patch({ valveInvert })}
          onSave={() => save.mutate()}
          dirty={dirty}
          saving={save.isPending}
        />
      )}

      {config.mode === 'schedule' && <ScheduleSection device={device} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verso della valvola bistabile (solo nodi LoRa)
// ---------------------------------------------------------------------------

/**
 * Quale polarita' apra la valvola dipende da come i due fili sono finiti su
 * OUT1/OUT2 del DRV8833, e il colore non lo dice: e' un dato del montaggio, non
 * del firmware. Per questo sta qui e non in uno sketch da ricompilare — il nodo
 * lo riceve dal server in ogni piano e in ogni comando.
 *
 * Sta in una card a parte, e non fra le soglie, perche' sbagliarlo non produce
 * un'irrigazione storta ma una valvola che si apre quando il sistema crede di
 * averla chiusa: in giardino, un allagamento che nessuno vede.
 */
function ValveDirectionCard({
  invert,
  onChange,
  onSave,
  dirty,
  saving,
}: {
  invert: boolean;
  onChange: (invert: boolean) => void;
  onSave: () => void;
  dirty: boolean;
  saving: boolean;
}) {
  return (
    <section className="card stack">
      <h3 style={{ margin: 0, fontSize: 15 }}>Verso della valvola</h3>
      <p className="small secondary" style={{ margin: 0 }}>
        La valvola bistabile ha due soli fili e si comanda invertendo la polarità: quale verso
        apra dipende da come sono collegati, e il colore non lo dice. Se il nodo fa il contrario
        di quello che dichiara, si corregge qui — senza toccare il cablaggio né riflashare.
      </p>

      <div className="row row-wrap" role="radiogroup" aria-label="Verso della valvola">
        {(
          [
            [false, 'IN1 apre', 'Cablaggio nominale: filo rosso su OUT1, nero su OUT2'],
            [true, 'IN2 apre', 'Fili scambiati sulle uscite del DRV8833'],
          ] as const
        ).map(([value, label, hint]) => (
          <button
            key={String(value)}
            role="radio"
            aria-checked={invert === value}
            className={`btn${invert === value ? ' btn--primary' : ''}`}
            onClick={() => onChange(value)}
            title={hint}
          >
            {label}
          </button>
        ))}
      </div>

      <p className="tiny secondary" style={{ margin: 0 }}>
        <strong>Come si verifica:</strong> premi «Irriga ora» per un minuto e guarda la valvola. Il
        LED del nodo lampeggia <strong>verde</strong> quando il firmware crede di aprire e{' '}
        <strong>rosso</strong> quando crede di chiudere: se lampeggia verde mentre l&apos;acqua non
        parte — o rosso mentre parte — il verso è quello sbagliato. Stesso discorso all&apos;avvio
        del nodo, che chiude sempre per sicurezza.
      </p>

      <div className="row">
        <button className="btn btn--primary" onClick={onSave} disabled={!dirty || saving}>
          Salva configurazione
        </button>
        <span className="tiny muted">
          Arriva al nodo al prossimo contatto, e comunque prima del comando successivo.
        </span>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Schedulazioni settimanali
// ---------------------------------------------------------------------------

function ScheduleSection({ device }: { device: DeviceDetail }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Schedule | 'new' | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['device', device.id] });

  const remove = useMutation({
    mutationFn: (scheduleId: number) => api.deleteSchedule(device.id, scheduleId),
    onSuccess: refresh,
  });

  const toggle = useMutation({
    mutationFn: (schedule: Schedule) =>
      api.updateSchedule(device.id, schedule.id, { ...schedule, enabled: !schedule.enabled }),
    onSuccess: refresh,
  });

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Fasce settimanali</h3>
        <button className="btn btn--sm" onClick={() => setEditing('new')}>
          + Aggiungi
        </button>
      </div>

      {device.schedules.length === 0 && editing === null && (
        <p className="small muted" style={{ margin: 0 }}>
          Nessuna fascia impostata: in modalità programmata il controller non irrigherà.
        </p>
      )}

      {device.schedules.map((schedule) =>
        editing !== 'new' && editing?.id === schedule.id ? (
          <ScheduleForm
            key={schedule.id}
            deviceId={device.id}
            schedule={schedule}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
          />
        ) : (
          <div key={schedule.id} className="row row-wrap" style={{ justifyContent: 'space-between' }}>
            <div className="row" style={{ gap: 10 }}>
              <input
                type="checkbox"
                checked={schedule.enabled}
                onChange={() => toggle.mutate(schedule)}
                aria-label={schedule.enabled ? 'Disattiva fascia' : 'Attiva fascia'}
              />
              <span style={{ opacity: schedule.enabled ? 1 : 0.5 }}>
                <strong>{formatTime(schedule.hour, schedule.minute)}</strong>{' '}
                <span className="secondary small">
                  · {formatDays(schedule.daysMask)} · {schedule.durationMinutes} min
                </span>
              </span>
            </div>
            <div className="row">
              <button className="btn btn--sm" onClick={() => setEditing(schedule)}>
                Modifica
              </button>
              <button className="btn btn--sm btn--danger" onClick={() => remove.mutate(schedule.id)}>
                Elimina
              </button>
            </div>
          </div>
        ),
      )}

      {editing === 'new' && (
        <ScheduleForm
          deviceId={device.id}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function ScheduleForm({
  deviceId,
  schedule,
  onDone,
}: {
  deviceId: string;
  schedule?: Schedule;
  onDone: () => void;
}) {
  const [daysMask, setDaysMask] = useState(schedule?.daysMask ?? ALL_DAYS);
  const [time, setTime] = useState(
    formatTime(schedule?.hour ?? 6, schedule?.minute ?? 30),
  );
  const [duration, setDuration] = useState(schedule?.durationMinutes ?? 5);

  const save = useMutation({
    mutationFn: () => {
      const [hour = 0, minute = 0] = time.split(':').map(Number);
      const input: ScheduleInput = {
        daysMask,
        hour,
        minute,
        durationMinutes: duration,
        enabled: schedule?.enabled ?? true,
      };
      return schedule
        ? api.updateSchedule(deviceId, schedule.id, input)
        : api.createSchedule(deviceId, input);
    },
    onSuccess: onDone,
  });

  return (
    <div className="stack" style={{ gap: 10, padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
      <div className="days">
        {DAY_LABELS.map((label, index) => {
          const bit = 1 << index;
          const active = (daysMask & bit) !== 0;
          return (
            <button
              key={label}
              aria-pressed={active}
              onClick={() => setDaysMask(daysMask ^ bit)}
              type="button"
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="fields">
        <Field label="Orario di avvio">
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </Field>
        <Field label="Durata (minuti)">
          <input
            type="number"
            min={CONFIG_LIMITS.scheduleDurationMinutes.min}
            max={CONFIG_LIMITS.scheduleDurationMinutes.max}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
          />
        </Field>
      </div>

      <div className="row">
        <button className="btn btn--primary btn--sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {schedule ? 'Salva' : 'Aggiungi'}
        </button>
        <button className="btn btn--sm" onClick={onDone}>
          Annulla
        </button>
      </div>
      {save.error && <Notice variant="error">{(save.error as Error).message}</Notice>}
    </div>
  );
}
